import { observer } from "mobx-react-lite";
import FileHelper from "../../../helpers/FileHelper";
import { Button, Input, Flex, Table } from 'antd';
import { SearchOutlined, CaretLeftFilled } from '@ant-design/icons';
import { ReactElement, useEffect, useState } from "react";
import { useStore } from "../../../root-store-context";
import useBreakpoint from "use-breakpoint";
import { ArchitecturalRequestsItem } from "../../../api/AccountSummary/AccountSummaryTypes";
import { useLocation, useNavigate } from "react-router-dom";
import getArchitecturalRequestsDocumentsColumns from "./ArchitecturalRequestsDocumentsColumns";
import Link from "antd/es/typography/Link";
import { ColumnsType } from "antd/es/table";
import { McShaneCommunity } from '../../../Enums/McShaneCommunity';


export type ArchitecturalRequestGroupedType = {
    key: string;
    requestNo: string;
    data: ArchitecturalRequestsItem[];
};

const ArchitecturalRequests = observer(() => {
    const BREAKPOINTS = { mobile: 0, desktop: 768 }
    const { breakpoint } = useBreakpoint(BREAKPOINTS, 'mobile')
    const { apiStore, architecturalRequestsStore, applicationStore, modalViewerStore } = useStore();
    const [searchText, setSearchText] = useState<string>("");

    const [filteredFiles, setFilteredFiles] = useState<ArchitecturalRequestsItem[]>([]);
    const [architecturalRequests, setArchitecturalRequests] = useState<ArchitecturalRequestsItem[]>([]);
    const [groupedArchitecturalRequests, setGroupedArchitecturalRequests] = useState<ArchitecturalRequestGroupedType[]>([]);

    const [isLoading, setIsLoading] = useState<boolean>(true);
    const navigate = useNavigate();
    const location = useLocation();

    const [sortRequest, setSortRequest] = useState<'ascend' | 'descend' | null>('ascend');
    const [sortStatus, setSortStatus] = useState<'ascend' | 'descend' | null>(null);
    const [sortStatusNotes, setSortStatusNotes] = useState<'ascend' | 'descend' | null>(null);
    const [sortRequestDate, setSortRequestDate] = useState<'ascend' | 'descend' | null>(null);
    const [sortApprovedDate, setSortApprovedDate] = useState<'ascend' | 'descend' | null>(null);
    const [isDisabled, setIsDisabled] = useState<boolean>(false);


    useEffect(() => {
        const sourceid = apiStore.configApi.community.communityId;
        // const disabled = Object.values(McShaneCommunity).includes(sourceid as McShaneCommunity);
        // setIsDisabled(disabled);
    }, [apiStore.configApi.community.communityId]);

    useEffect(() => {
        if (searchText === "") {
            setFilteredFiles([]);
            return;
        } else {
            setFilteredFiles(architecturalRequests.filter((i) =>
                String(i.requestNo).includes(searchText.toLocaleLowerCase()) ||
                i.description?.toLocaleLowerCase().includes(searchText.toLocaleLowerCase())));
        }
    }, [searchText])

    useEffect(() => {
        setIsLoading(true);
        apiStore.AccountSummaryApiClient.getArchitecturalRequests()
            .then(res => {
                const groupedData: { [type: string]: ArchitecturalRequestsItem[] } = {};
                res.forEach((item) => {
                    const type = item.description;
                    if (!groupedData[type]) {
                        groupedData[type] = [];
                    }
                    groupedData[type].push({ ...item, key: Math.random().toString(36).substr(2, 10) } as ArchitecturalRequestsItem);
                });
                const result = Object.keys(groupedData).map(type => {
                    return {
                        key: Math.random().toString(36).substr(2, 10),
                        requestNo: type,
                        data: groupedData[type].map((d, i) => ({ ...d, key: i }))
                    };
                })
                setGroupedArchitecturalRequests(result);
                setArchitecturalRequests(res.map((d, i) => ({ ...d, key: i })))
                setIsLoading(false);
            }).catch((error) => {
                setIsLoading(false);
                console.log("getArchitecturalRequests: ", error);
            });
    }, [])

    const downloadFilesButton = (data: ArchitecturalRequestsItem): ReactElement => {
        if (data.files) {
            return <Flex vertical justify="flex-start" align="flex-start">
                {data.files.map((el, index) => {
                    return (
                        <Button
                            style={{ textDecoration: "underline", flex: "1 1 100%", textAlign: "left", padding: 4 }}
                            type="text"
                            block
                            key={index}
                            onClick={async () => {
                                modalViewerStore.openFileWithLoading(apiStore.AccountSummaryApiClient.getAttachmentArchtectStringData(el.ArchitecturalNoteKey.toString(), el.FileName), el.FileName, setIsLoading);
                            }
                            }>
                            <Link style={{ whiteSpace: "wrap" }}>{el.FileName}</Link>
                        </Button>
                    )
                })}
            </Flex>
        } else {
            return (
                <Flex justify="center">
                    <Button onClick={async () => {
                        setIsLoading(true);
                        const res = await apiStore.AccountSummaryApiClient.getArchitecturalAttaches(data.requestNo.toString());
                        data.files = res;
                        setIsLoading(false);
                    }}>Download files</Button>
                </Flex>
            )
        }
    }

    const groupedTypeColumns: ColumnsType<ArchitecturalRequestGroupedType> = [
        {
            key: "3",
            title: "Request #",
            width: "13%",
            dataIndex: "requestNo",
            render: (data) => <div style={{ position: "absolute", width: "280px", top: "50%", transform: "translateY(-50%)", zIndex: "1" }}>{data}</div>,
            defaultSortOrder: sortRequest,
            sorter: (a, b, direction) => {
                if (direction === 'ascend') {
                    setSortRequest(direction);
                } else if (direction === 'descend') {
                    setSortRequest(direction);
                }
                // setSortRequest(null);
                setSortStatus(null);
                setSortStatusNotes(null);
                setSortRequestDate(null);
                setSortApprovedDate(null);
                return 0;
            },
            onHeaderCell: () => (
                {
                    onClick: (event: React.MouseEvent) => {
                        if (sortRequest === 'descend') {
                            setSortRequest(null);
                        }
                    },
                }
            )
        },
        {
            key: "4",
            title: "Status",
            dataIndex: "status",
            defaultSortOrder: sortStatus,
            sorter: (a, b, direction) => {
                if (direction === 'ascend') {
                    setSortStatus(direction);
                } else if (direction === 'descend') {
                    setSortStatus(direction);
                }
                setSortRequest(null);
                // setSortStatus(null);
                setSortStatusNotes(null);
                setSortRequestDate(null);
                setSortApprovedDate(null);
                return 0;
            },
            onHeaderCell: () => (
                {
                    onClick: (event: React.MouseEvent) => {
                        if (sortStatus === 'descend') {
                            setSortStatus(null);
                        }
                    },
                }
            )
        },
        {
            key: "5",
            title: "Status Notes",
            dataIndex: "statusNotes",
            defaultSortOrder: sortStatusNotes,
            sorter: (a, b, direction) => {
                if (direction === 'ascend') {
                    setSortStatusNotes(direction);
                } else if (direction === 'descend') {
                    setSortStatusNotes(direction);
                }
                setSortRequest(null);
                setSortStatus(null);
                // setSortStatusNotes(null);
                setSortRequestDate(null);
                setSortApprovedDate(null);
                return 0;
            },
            onHeaderCell: () => (
                {
                    onClick: (event: React.MouseEvent) => {
                        if (sortStatusNotes === 'descend') {
                            setSortStatusNotes(null);
                        }
                    },
                }
            )
        },
        {
            key: "6",
            title: "Request Date",
            dataIndex: "requestDate",
            defaultSortOrder: sortRequestDate,
            sorter: (a, b, direction) => {
                if (direction === 'ascend') {
                    setSortRequestDate(direction);
                } else if (direction === 'descend') {
                    setSortRequestDate(direction);
                }
                setSortRequest(null);
                setSortStatus(null);
                setSortStatusNotes(null);
                // setSortRequestDate(null);
                setSortApprovedDate(null);
                return 0;
            },
            onHeaderCell: () => (
                {
                    onClick: (event: React.MouseEvent) => {
                        if (sortRequestDate === 'descend') {
                            setSortRequestDate(null);
                        }
                    },
                }
            )
        },
        {
            key: "7",
            title: "Approved Date",
            dataIndex: "approvedDate",
            defaultSortOrder: sortApprovedDate,
            sorter: (a, b, direction) => {
                if (direction === 'ascend') {
                    setSortApprovedDate(direction);
                } else if (direction === 'descend') {
                    setSortApprovedDate(direction);
                }
                setSortRequest(null);
                setSortStatus(null);
                setSortStatusNotes(null);
                setSortRequestDate(null);
                // setSortApprovedDate(null);
                return 0;
            },
            onHeaderCell: () => (
                {
                    onClick: (event: React.MouseEvent) => {
                        if (sortApprovedDate === 'descend') {
                            setSortApprovedDate(null);
                        }
                    },
                }
            )
        },
        {
            key: "download",
            title: "Files",
            dataIndex: "isFolder",
        },
    ];
 
    const defaultColums = getArchitecturalRequestsDocumentsColumns(downloadFilesButton)
    const architecturalFeature = applicationStore.AllFeaturesForCommunity?.find(
    (f) => f.featureId === 14
    );

    return     <>
        {isDisabled && (
            <p className="disabled-message">
                Please continue to submit work order requests at -{" "}
                <a
                    href="https://www.mcshanemanagement.com/owners/work-order/"
                    target="_blank"
                    className="disabled-link"
                >
                    Work Order - McShane & Associates Owners
                </a>.
                <br />
                You will be notified when the process has been updated.
            </p>
        )}

        {!isDisabled && (
            <>
                <Flex justify="space-between" wrap="wrap" align="center" gap="8px" style={{ paddingBottom: 24 }}>
                    <Flex wrap="wrap" align="center" gap="16px">
                        {architecturalFeature &&
                            <h1 className="page-title">Architectural Requests</h1>
                        }
                        <Input
                            value={searchText}
                            className="serach-bar"
                            onChange={(e) => setSearchText(e.target.value)}
                            prefix={<Button shape="circle" type="text" icon={<SearchOutlined />} />}
                            variant="borderless"
                            placeholder="Search"
                            allowClear
                        />
                    </Flex>

                    {architecturalFeature?.isActive && !architecturalFeature?.isViewOnly &&  (
                    <Button size="middle" type="primary"
                        onClick={() => { navigate(`${location.pathname}/Add`); }}>Add</Button>
                    )}
                </Flex>

                <Table
                    className="table-wrapper"
                    loading={isLoading}
                    scroll={{ x: process.env.REACT_APP_TABLE_SCROLL_WIDTH }}
                    columns={breakpoint === "mobile" ? defaultColums.small : defaultColums.large}
                    dataSource={searchText === "" ? architecturalRequests : filteredFiles}
                />
            </>
        )}
    </>
})

export default ArchitecturalRequests